import {Button} from '@orbit-ui/components/button';
import {Card, CardContent, CardHeader, CardTitle} from '@orbit-ui/components/card';
import {Checkbox} from '@orbit-ui/components/checkbox';
import {Input} from '@orbit-ui/components/input';
import {Label} from '@orbit-ui/components/label';
import {PageHeader} from '@orbit-ui/components/page-header';
import {RadioGroup, RadioGroupItem} from '@orbit-ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@orbit-ui/components/select';
import {Separator} from '@orbit-ui/components/separator';
import {Switch} from '@orbit-ui/components/switch';
import {Textarea} from '@orbit-ui/components/textarea';
import * as React from 'react';

export const ApplicationsConfigurationView = () => {
  const [activeSection, setActiveSection] = React.useState('general');

  return (
    <div className="container mx-auto space-y-6 py-6">
      <PageHeader
        title="Applications Configuration"
        description="Configure application settings and deployment preferences"
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        {/* Navigation Menu */}
        <div className="space-y-2">
          <Card>
            <CardContent className="p-4">
              <RadioGroup
                value={activeSection}
                onValueChange={setActiveSection}
                className="space-y-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="general" id="config-general" />
                  <Label htmlFor="config-general" className="cursor-pointer font-medium">
                    General
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="deployment" id="config-deployment" />
                  <Label htmlFor="config-deployment" className="cursor-pointer font-medium">
                    Deployment
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="scaling" id="config-scaling" />
                  <Label htmlFor="config-scaling" className="cursor-pointer font-medium">
                    Scaling
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="networking" id="config-networking" />
                  <Label htmlFor="config-networking" className="cursor-pointer font-medium">
                    Networking
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
        </div>

        {/* Content Area */}
        <div className="col-span-1 space-y-4 md:col-span-3">
          {/* General Settings */}
          {activeSection === 'general' && (
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="default-environment">Default Environment</Label>
                    <Select defaultValue="production">
                      <SelectTrigger id="default-environment">
                        <SelectValue placeholder="Select default environment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="production">Production</SelectItem>
                        <SelectItem value="staging">Staging</SelectItem>
                        <SelectItem value="development">Development</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="app-naming-convention">Application Naming Convention</Label>
                    <Input
                      id="app-naming-convention"
                      placeholder="e.g., [env]-[name]-[version]"
                      defaultValue="[env]-[name]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="default-labels">Default Labels</Label>
                  <Textarea
                    id="default-labels"
                    placeholder="key1=value1,key2=value2"
                    defaultValue="managed-by=cosmonic,created-by=ui"
                  />
                </div>

                <Separator className="my-4" />

                <div className="space-y-2">
                  <Label>Default Behaviors</Label>
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    <div className="flex items-center space-x-2">
                      <Switch id="auto-scaling" defaultChecked />
                      <Label htmlFor="auto-scaling">Enable auto-scaling by default</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch id="health-checks" defaultChecked />
                      <Label htmlFor="health-checks">Enable health checks by default</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch id="rolling-updates" defaultChecked />
                      <Label htmlFor="rolling-updates">Use rolling updates by default</Label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Deployment Settings */}
          {activeSection === 'deployment' && (
            <Card>
              <CardHeader>
                <CardTitle>Deployment Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="deployment-strategy">Default Deployment Strategy</Label>
                    <Select defaultValue="rolling">
                      <SelectTrigger id="deployment-strategy">
                        <SelectValue placeholder="Select deployment strategy" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rolling">Rolling Update</SelectItem>
                        <SelectItem value="recreate">Recreate</SelectItem>
                        <SelectItem value="blue-green">Blue/Green</SelectItem>
                        <SelectItem value="canary">Canary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="default-replicas">Default Replicas</Label>
                    <Input id="default-replicas" type="number" defaultValue="2" min="1" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Deployment Checks</Label>
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="security-scan" defaultChecked />
                      <Label htmlFor="security-scan">Run security scan before deployment</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="dependency-check" defaultChecked />
                      <Label htmlFor="dependency-check">Validate dependencies</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="resource-check" defaultChecked />
                      <Label htmlFor="resource-check">Validate resource requirements</Label>
                    </div>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-2">
                  <Label>Rollback Settings</Label>
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    <div className="flex items-center space-x-2">
                      <Switch id="auto-rollback" defaultChecked />
                      <Label htmlFor="auto-rollback">Automatic rollback on failure</Label>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="keep-revisions">Deployment revisions to keep</Label>
                      <Select defaultValue="5">
                        <SelectTrigger id="keep-revisions">
                          <SelectValue placeholder="Select number of revisions" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 revisions</SelectItem>
                          <SelectItem value="5">5 revisions</SelectItem>
                          <SelectItem value="10">10 revisions</SelectItem>
                          <SelectItem value="all">All revisions</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scaling Settings */}
          {activeSection === 'scaling' && (
            <Card>
              <CardHeader>
                <CardTitle>Scaling Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="min-replicas">Default Minimum Replicas</Label>
                    <Input id="min-replicas" type="number" defaultValue="1" min="1" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="max-replicas">Default Maximum Replicas</Label>
                    <Input id="max-replicas" type="number" defaultValue="10" min="1" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cpu-threshold">CPU Utilization Threshold (%)</Label>
                    <Input id="cpu-threshold" type="number" defaultValue="80" min="1" max="100" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="memory-threshold">Memory Utilization Threshold (%)</Label>
                    <Input
                      id="memory-threshold"
                      type="number"
                      defaultValue="80"
                      min="1"
                      max="100"
                    />
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-2">
                  <Label>Scaling Policies</Label>
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    <div className="flex items-center space-x-2">
                      <Switch id="scale-to-zero" />
                      <Label htmlFor="scale-to-zero">Allow scaling to zero</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch id="predictive-scaling" />
                      <Label htmlFor="predictive-scaling">Enable predictive scaling</Label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Networking Settings */}
          {activeSection === 'networking' && (
            <Card>
              <CardHeader>
                <CardTitle>Networking Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="service-type">Default Service Type</Label>
                    <Select defaultValue="clusterip">
                      <SelectTrigger id="service-type">
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clusterip">ClusterIP</SelectItem>
                        <SelectItem value="nodeport">NodePort</SelectItem>
                        <SelectItem value="loadbalancer">LoadBalancer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ingress-class">Default Ingress Class</Label>
                    <Select defaultValue="nginx">
                      <SelectTrigger id="ingress-class">
                        <SelectValue placeholder="Select ingress class" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nginx">NGINX</SelectItem>
                        <SelectItem value="traefik">Traefik</SelectItem>
                        <SelectItem value="istio">Istio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Network Policies</Label>
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="default-deny" />
                      <Label htmlFor="default-deny">Apply default deny policy</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="same-namespace" defaultChecked />
                      <Label htmlFor="same-namespace">Allow same-namespace communication</Label>
                    </div>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-2">
                  <Label>DNS Settings</Label>
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="dns-policy">DNS Policy</Label>
                      <Select defaultValue="cluster-first">
                        <SelectTrigger id="dns-policy">
                          <SelectValue placeholder="Select DNS policy" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cluster-first">ClusterFirst</SelectItem>
                          <SelectItem value="default">Default</SelectItem>
                          <SelectItem value="none">None</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline">Reset to Defaults</Button>
        <Button>Save Changes</Button>
      </div>
    </div>
  );
};
